import React, { useEffect, useState } from "react";
import { Drawer, Form, Input, Button, message, Select, Row, Col } from "antd";
import API from "../../api";

const { Option } = Select;

const BankAccountDrawer = ({ open, setOpen, data, isEdit, onSubmit, stores }) => {
    const [form] = Form.useForm();
    const [isLoading, setLoading] = useState(false);

    useEffect(() => {
        if (open) {
            if (isEdit && data) {
                form.setFieldsValue({
                    ...data,
                    storeId: data.storeId,
                });
            } else {
                form.resetFields();
            }
        }
    }, [open, isEdit, data, form]);

    const onFinish = async (values) => {
        setLoading(true);
        try {
            let response;
            if (isEdit) {
                response = await API.put(`/bank-accounts/${data?._id}`, values);
                message.success("Bank account updated successfully!");
                onSubmit({ ...values, _id: data?._id });
            } else {
                response = await API.post(`/bank-accounts`, values);
                message.success("Bank account added successfully!");
                onSubmit(response?.data);
            }
            setOpen(false);
        } catch (err) {
            console.error(err);
            message.error(err.response?.data?.message || "Failed to process the request.");
        } finally {
            setLoading(false);
        }
    };

    const accountTypeOptions = [
        'Current',
        'Savings', 
        'Business',
        'Salary',
        'Fixed Deposit',
        'Recurring Deposit',
        'Others'
    ];

    return (
        <Drawer
            title={isEdit ? "Edit Bank Account" : "Add Bank Account"}
            width={700}
            placement="right"
            onClose={() => setOpen(false)}
            open={open}
        >
            <Form layout="vertical" form={form} onFinish={onFinish} autoComplete="off">
                {isEdit && (
                    <Form.Item name="_id" hidden>
                        <Input type="hidden" />
                    </Form.Item>
                )}

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            label="Select Branch/Store"
                            name="storeId"
                            rules={[{ required: true, message: "Please select a branch/store." }]}
                        >
                            <Select
                                placeholder="Select Branch/Store"
                                allowClear
                            >
                                {stores.map((store) => (
                                    <Select.Option key={store._id} value={store._id}>
                                        {store.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            label="Account Name"
                            name="accountName"
                            rules={[{ required: true, message: "Please enter account name." }]}
                        >
                            <Input placeholder="e.g., Main Business Account" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            label="Bank Name"
                            name="bankName"
                            rules={[{ required: true, message: "Please enter bank name." }]}
                        >
                            <Input placeholder="e.g., HDFC Bank" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            label="Account Number"
                            name="accountNumber"
                            rules={[{ required: true, message: "Please enter account number." }]}
                        >
                            <Input placeholder="e.g., 1234567890" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            label="IFSC Code"
                            name="ifscCode"
                            rules={[{ required: true, message: "Please enter IFSC code." }]}
                        >
                            <Input placeholder="e.g., HDFC0001234" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            label="UPI ID"
                            name="upiId"
                        >
                            <Input placeholder="e.g., branch@hdfc" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            label="Account Type"
                            name="accountType"
                            rules={[{ required: true, message: "Please select account type." }]}
                        >
                            <Select
                                placeholder="Select Account Type"
                                allowClear
                            >
                                {accountTypeOptions.map((type) => (
                                    <Select.Option key={type} value={type}>
                                        {type}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            label="Status"
                            name="isActive"
                            initialValue={true}
                        >
                            <Select>
                                <Option value={true}>Active</Option>
                                <Option value={false}>Inactive</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item
                    label="Remarks"
                    name="remarks"
                >
                    <Input.TextArea rows={3} placeholder="Additional notes or comments..." />
                </Form.Item>

                <Form.Item>
                    <Button type="primary" block htmlType="submit" loading={isLoading}>
                        {isEdit ? "Update Bank Account" : "Add Bank Account"}
                    </Button>
                </Form.Item>
            </Form>
        </Drawer>
    );
};

export default BankAccountDrawer;
